import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAcquisitionOffers } from './api';
import { AcquisitionOffer, PricingCatalogErrorCode } from './types';
import { PRICING_CATALOG_ACQUISITION_QUERY_KEY } from './queryKeys';

export { PRICING_CATALOG_ACQUISITION_QUERY_KEY };

type UsePricingCatalogAcquisitionParams = {
  coupon?: string;
  enabled: boolean;
};

const getErrorCode = (error: unknown): PricingCatalogErrorCode | undefined =>
  (error as { response?: { data?: { code?: PricingCatalogErrorCode } } })?.response?.data?.code;

export function usePricingCatalogAcquisition({
  coupon,
  enabled,
}: UsePricingCatalogAcquisitionParams) {
  // um cupom rejeitado nunca vai ficar válido na mesma sessão; assim
  // que a query com ele falha como "cupom inválido", cai pro catálogo base (sem cupom) em vez de
  // depender de um estado "bom" anterior que pode nunca ter existido (ex.: link OAB com cupom
  // inválido já no primeiro fetch).
  const [rejectedCoupon, setRejectedCoupon] = useState<string | undefined>(undefined);
  const effectiveCoupon = coupon && coupon !== rejectedCoupon ? coupon : undefined;
  const queryKey = [...PRICING_CATALOG_ACQUISITION_QUERY_KEY, effectiveCoupon || null] as const;

  const { data, isLoading, isError, error, refetch: refetchQuery } = useQuery<AcquisitionOffer[]>({
    queryKey,
    queryFn: () => getAcquisitionOffers(effectiveCoupon),
    enabled,
    // O cupom faz parte da queryKey: aplicar um cupom cria uma query sem dados, e sem isto o
    // `isLoading` volta a true e o checkout troca tudo por "Carregando planos..." (piscada, e
    // desmonta o PlanSummary com o estado local dele). Mantém o catálogo anterior na tela até
    // o preço com cupom chegar. Só vale depois do 1º load: sem dado anterior segue carregando.
    keepPreviousData: true,
    // Erro bloqueante com retry explícito do dev (decisão do refinamento) — sem retry
    // automático silencioso escondendo a falha atrás de latência.
    retry: false,
  });

  const errorCode = isError ? getErrorCode(error) : undefined;

  useEffect(() => {
    if (isError && errorCode === 'PRICING_CATALOG_COUPON_INVALID' && effectiveCoupon) {
      setRejectedCoupon(effectiveCoupon);
    }
  }, [isError, errorCode, effectiveCoupon]);

  // Calling the raw React Query refetch() here was a no-op
  // once a coupon had been rejected: the query key had already dropped it (effectiveCoupon
  // undefined), so "Tentar novamente" only re-ran the couponless query. Clearing rejectedCoupon
  // moves the query key back to the original coupon on the next render, which React Query
  // (staleTime 0 by default) fetches automatically — a manual refetch() here would still target
  // the stale, couponless key from this closure.
  // a non-coupon error (network down, PRICING_CATALOG_INCOMPLETE) has no
  // rejectedCoupon to clear, so the branch above was a silent no-op; fall back to the query's own
  // refetch to actually re-issue the request.
  const retry = () => {
    if (rejectedCoupon) {
      setRejectedCoupon(undefined);
      return;
    }
    refetchQuery();
  };

  // divergência catálogo×validate (catálogo aceita, /coupons/validate rejeita) não é um
  // erro desta query — este hook não conhece /coupons/validate. O caller (useCheckoutPlans/
  // Cadastro) detecta a divergência e chama rejectCoupon pra acionar o mesmo self-heal do cupom
  // rejeitado pelo próprio catálogo (cai pro catálogo base).
  const rejectCoupon = (code: string) => {
    setRejectedCoupon(code);
  };

  return {
    offers: data,
    isLoading,
    isError,
    error,
    errorCode,
    isCouponRejected: Boolean(coupon) && coupon === rejectedCoupon,
    refetch: retry,
    rejectCoupon,
  };
}
